import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, Button, Stack, Typography } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import NotFoundd from "../Images/notFound.png";

const easeOut = [0.22, 1, 0.36, 1];

const MotionStack = motion.create(Stack);

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.14, delayChildren: 0.08 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut },
  },
};

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 6, md: 8 },
        px: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 960,
          display: "grid",
          gap: { xs: 4, md: 6 },
          alignItems: "center",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <MotionStack
          spacing={2.5}
          variants={stagger}
          initial="hidden"
          animate="visible"
          sx={{ textAlign: { xs: "center", md: "left" } }}
        >
          <motion.div variants={fadeUp}>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: "0.35em",
                fontWeight: 600,
              }}
            >
              Page not found
            </Typography>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.75rem" },
              }}
            >
              We couldn&apos;t find that page.
            </Typography>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 440,
                mx: { xs: "auto", md: 0 },
              }}
            >
              The address may be incorrect, or the page may have been moved or
              removed. Check the URL or use the button below to continue.
            </Typography>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Box
              sx={{
                pt: 1,
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Button
                component={RouterLink}
                to="/"
                variant="contained"
                size="large"
                startIcon={<HomeOutlinedIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  px: 3,
                  fontWeight: 600,
                }}
              >
                Go back
              </Button>
            </Box>
          </motion.div>
        </MotionStack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.65, delay: 0.2, ease: easeOut }}
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                component="img"
                src={NotFoundd}
                alt="Page not found illustration"
                sx={{
                  width: { xs: 240, sm: 300, md: 340 },
                  height: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  filter: (theme) =>
                    theme.palette.mode === "light"
                      ? "drop-shadow(0 16px 32px rgba(0,0,0,0.12))"
                      : "drop-shadow(0 24px 48px rgba(0,0,0,0.35))",
                }}
              />
            </motion.div>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default NotFound;
